import {
  WIZARD_TYPES,
  getBillingPeriodCode,
  getWizardConfig,
  getWizardSteps,
} from '../../constants/planWizardTypes';

const costRequired = (policy) => policy?.code === 'FIXED';

export const validateCost = (data, policy, t, amountCurrency, getFixedAmountRangeError) => {
  const errors = {};
  if (!costRequired(policy)) {
    return errors;
  }
  if (!data.cost && policy?.isFlexible === 'false') {
    errors.cost = t('products.validate_cost_error');
  }
  if (data.cost < 0 && data.cost) {
    errors.cost = t('products.negative_value_error');
  }
  if (data.cost < 1 && data.cost) {
    errors.cost = getFixedAmountRangeError(amountCurrency, '1.00', '1,000.00');
  }
  if (data.cost && !data.cost) {
    errors.cost = t('products.validate_cost_error');
  }
  if (data.cost === 0 || data.cost > 10000) {
    errors.cost = getFixedAmountRangeError(amountCurrency, '1.00', '1,000.00');
  }
  return errors;
};

export const validateSetup = (data, t, getFixedAmountRangeError, amountCurrency, hideSetupFee = false) => {
  const errors = {};
  if (
    hideSetupFee
    || data.autoBilling
    || getBillingPeriodCode(data.billingPeriod) === 'ONCE'
    || !data.feeCheck
  ) {
    return errors;
  }
  if (data.setup < 0 && data.feeCheck) {
    errors.setup = t('products.negative_value_error');
  }
  if (data.setup < 1 && data.feeCheck) {
    errors.setup = getFixedAmountRangeError(amountCurrency, '1.00', '1,000.00');
  }
  if (data.feeCheck && !data.setup) {
    errors.setup = t('products.setup_validate_error');
  }
  if (data.setup === 0 || data.setup > 1000) {
    if (data.feeCheck) {
      errors.setup = getFixedAmountRangeError(amountCurrency, '1.00', '1,000.00');
    }
  }
  return errors;
};

export const validateDeductions = (data, wizardType, business, t, getBillingPeriodMax) => {
  const errors = {};
  const config = getWizardConfig(wizardType);

  if (config.hideDeductions) {
    return errors;
  }

  if (!data.deductions && wizardType === WIZARD_TYPES.TARGET) {
    errors.deductions = business.purpose === 'SERVICES'
      ? t('plan.deductions_placeholder_payments')
      : t('plan.deductions_placeholder_donations');
  }
  if (data.billingPeriod?.code !== 'ONCE' && (data.deductions === 1 || data.deductions === 0)) {
    if (wizardType === WIZARD_TYPES.TARGET || data.deductions === 0 || data.deductions === 1) {
      errors.deductions = business.purpose === 'SERVICES'
        ? t('products.deduction_error_payments_min')
        : t('products.deduction_error_donations_min');
    }
  }
  if (data.deductions < 0) {
    errors.deductions = t('products.negative_value_error');
  }
  if (data.deductions > getBillingPeriodMax()) {
    errors.deductions = t('');
  }
  return errors;
};

const validateRemindersFields = (data, t) => {
  const errors = {};
  if (data.reminderDays < 0) {
    errors.reminderDays = t('products.negative_value_error');
  }
  if (data.reminderDays && (Number(data.reminderDays) < 1 || Number(data.reminderDays) > 7)) {
    errors.reminderDays = t('products.reminder_days_message');
  }
  if (data.graceDays < 0) {
    errors.graceDays = t('products.negative_value_error');
  }
  if (data.graceDays && (Number(data.graceDays) < 1 || Number(data.graceDays) > 7)) {
    errors.graceDays = t('products.grace_days_message');
  }
  return errors;
};

export const validatePlanWizardStep = ({
  wizardType,
  stepIndex,
  stepId,
  data,
  t,
  planGroupExist,
  planGroups,
  business,
  amountCurrency,
  getFixedAmountRangeError,
  getBillingPeriodMax,
}) => {
  const errors = {};
  const config = getWizardConfig(wizardType);
  const resolvedStepId = stepId ?? getWizardSteps(wizardType, data.billingPeriod)[stepIndex]?.id;

  if (resolvedStepId === 'details') {
    if (!data.name?.trim()) {
      errors.name = t('products.validate_error');
    }
    if (!planGroupExist && planGroups?.length > 1 && !data.planId) {
      errors.planId = t('products.validate_plan_error');
    }
    return errors;
  }

  if (resolvedStepId === 'amount') {
    if (!data.policy) {
      errors.policy = t('products.validate_policy_error');
    }

    Object.assign(errors, validateCost(data, data.policy, t, amountCurrency, getFixedAmountRangeError));

    if (!config.hidePeriod && !data.billingPeriod) {
      errors.billingPeriod = t('products.validate_billing_error');
    }

    Object.assign(errors, validateSetup(data, t, getFixedAmountRangeError, amountCurrency, config.hideSetupFee));
    Object.assign(errors, validateDeductions(data, wizardType, business, t, getBillingPeriodMax));
    return errors;
  }

  if (resolvedStepId === 'reminders') {
    return validateRemindersFields(data, t);
  }

  return errors;
};

export const validatePlanWizardFull = (params) => {
  const { wizardType, data } = params;
  const steps = getWizardSteps(wizardType, data.billingPeriod);
  let allErrors = {};

  steps.forEach((step, stepIndex) => {
    if (step.id === 'review') {
      return;
    }
    allErrors = {
      ...allErrors,
      ...validatePlanWizardStep({ ...params, stepIndex, stepId: step.id }),
    };
  });

  return allErrors;
};
